import {
	type ReactElement,
	type ReactNode,
	type RefObject,
	useEffect,
	useRef,
	useState,
} from "react";

import { Spinner } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { AutoScrollArea } from "../../../bottom-scroll";

interface LazyLoadingState {
	isLoadingMoreMessages: boolean;
	oldHeightBeforeLazyLoading: number;
}

interface ChatMessagesWrapperProps {
	readonly children: ReactNode;
	readonly className?: string;
	readonly hasMoreMessages?: boolean;
	readonly fetchMoreMessages?: () => Promise<void>;
	readonly emptyComponent?: ReactElement;
	readonly isFetchingMoreMessages?: boolean;
	readonly scrollAreaRef?: RefObject<HTMLDivElement | null>;
	readonly isAutoScrollOnMount?: boolean;
}

export default function ChatMessagesWrapper({
	children,
	className,
	hasMoreMessages,
	fetchMoreMessages,
	emptyComponent,
	isFetchingMoreMessages,
	scrollAreaRef: externalScrollAreaRef,
	isAutoScrollOnMount,
}: ChatMessagesWrapperProps) {
	const internalScrollAreaRef = useRef<HTMLDivElement>(null);
	const scrollAreaRef = externalScrollAreaRef ?? internalScrollAreaRef;
	const fetchMoreMessagesRef = useRef(fetchMoreMessages);

	fetchMoreMessagesRef.current = fetchMoreMessages;

	const [lazyLoading, setLazyLoading] = useState<LazyLoadingState>({
		isLoadingMoreMessages: false,
		oldHeightBeforeLazyLoading: 0,
	});

	useEffect(() => {
		if (lazyLoading.isLoadingMoreMessages && hasMoreMessages && fetchMoreMessages) {
			void fetchMoreMessages().then(() => {
				setLazyLoading((prev) => ({ ...prev, isLoadingMoreMessages: false }));
			});
		}
	}, [lazyLoading.isLoadingMoreMessages, hasMoreMessages, fetchMoreMessages]);

	useEffect(() => {
		if (scrollAreaRef.current) {
			const { isLoadingMoreMessages, oldHeightBeforeLazyLoading } = lazyLoading;
			const { scrollHeight } = scrollAreaRef.current;

			if (!isLoadingMoreMessages && oldHeightBeforeLazyLoading !== 0) {
				const heightDiff = scrollHeight - oldHeightBeforeLazyLoading - 96;

				scrollAreaRef.current.scrollTo({ top: heightDiff });
			}
		}
	}, [lazyLoading]);

	useEffect(() => {
		const element = scrollAreaRef.current;

		if (!element) {
			return;
		}

		function handleScroll() {
			if (!element) return;

			const { scrollTop, scrollHeight } = element;

			if (scrollTop === 0 && fetchMoreMessagesRef.current) {
				setLazyLoading({
					isLoadingMoreMessages: true,
					oldHeightBeforeLazyLoading: scrollHeight,
				});
			}
		}

		element.addEventListener("scroll", handleScroll);

		return () => {
			element.removeEventListener("scroll", handleScroll);
		};
	}, []);

	const noChildren =
		!children || // if no children
		(Array.isArray(children) && !children.length) || // or if children is an empty array
		(Array.isArray(children) &&
			children.every((child) => {
				// or if all children are falsy
				if (Array.isArray(child)) {
					// including nested arrays
					return !child.length;
				}

				return !child;
			}));

	if (noChildren && emptyComponent) {
		return emptyComponent;
	}

	return (
		<div className="relative flex-1 overflow-hidden">
			<AutoScrollArea
				ref={scrollAreaRef}
				className="no-scrollbar w-full"
				buttonId="chat-scrollbutton"
				isAutoScrollOnMount={isAutoScrollOnMount}
			>
				<div
					className={cn(
						"relative mx-auto flex min-h-full w-full max-w-2xl flex-col content-start px-10 py-9 xl:max-w-3xl",
						className,
					)}
				>
					{isFetchingMoreMessages && (
						<div className="flex flex-row items-center justify-center py-10">
							<Spinner size="sm" />
						</div>
					)}
					{children}
				</div>
			</AutoScrollArea>
		</div>
	);
}
